import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Minus, Plus, MailWarning } from 'lucide-react';
import { useAuth } from '../lib/auth';
import { formatZAR } from '../lib/format';
import InlineAuthDialog from '../components/InlineAuthDialog';

const PRICE = 5;

export default function BuyTicket() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [qty, setQty] = useState(1);
  const [authOpen, setAuthOpen] = useState(false);

  const total = qty * PRICE;
  const loggedInUnverified = !!user && !user.email_verified;

  const proceed = () => {
    if (!user) {
      setAuthOpen(true);
      return;
    }
    if (loggedInUnverified) return; // button is disabled; safety
    navigate(`/payment/mock?qty=${qty}`);
  };

  const onAuthSuccess = (_u, { justSignedUp }) => {
    setAuthOpen(false);
    const suffix = justSignedUp ? '&newAccount=1' : '';
    navigate(`/payment/mock?qty=${qty}${suffix}`);
  };

  return (
    <div data-testid="buy-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
      <p className="text-xs uppercase tracking-[0.32em] text-[#D4AF37] font-semibold mb-3">Buy tickets</p>
      <h1 className="font-serif-display text-4xl sm:text-5xl font-black">
        <span className="text-gold-gradient">Enter the Golden Catch</span>
      </h1>
      <p className="mt-3 text-[#C0C0C0]">R5 per ticket. This Friday. R10,000 up for grabs.</p>

      {loggedInUnverified && (
        <div
          data-testid="verify-banner"
          className="mt-8 card-embossed p-5 border-l-4 border-l-[#D4AF37] flex items-start gap-3"
        >
          <MailWarning className="text-[#D4AF37] mt-1" size={22} />
          <div>
            <p className="font-semibold text-white">Verify your email first</p>
            <p className="text-sm text-[#C0C0C0] mt-1">
              We sent a verification link to <span className="text-[#D4AF37]">{user.email}</span>. Purchases are
              disabled until your email is verified.
            </p>
          </div>
        </div>
      )}

      <div className="mt-10 card-embossed p-8 sm:p-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <p className="text-sm uppercase tracking-[0.24em] text-[#C0C0C0]/70">Quantity</p>
            <div className="mt-4 flex items-center gap-4">
              <button
                data-testid="qty-decrease"
                aria-label="Decrease"
                onClick={() => setQty((q) => Math.max(1, q - 1))}
                className="h-12 w-12 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37]/10"
              >
                <Minus size={20} />
              </button>
              <input
                data-testid="qty-input"
                type="number"
                min={1}
                max={100}
                value={qty}
                onChange={(e) => {
                  const v = parseInt(e.target.value || '1', 10);
                  if (Number.isFinite(v)) setQty(Math.min(100, Math.max(1, v)));
                }}
                className="w-24 text-center font-serif-display text-4xl bg-transparent text-white outline-none border-b border-[#D4AF37]/40 focus:border-[#D4AF37]"
              />
              <button
                data-testid="qty-increase"
                aria-label="Increase"
                onClick={() => setQty((q) => Math.min(100, q + 1))}
                className="h-12 w-12 rounded-full border border-[#D4AF37]/40 text-[#D4AF37] flex items-center justify-center hover:bg-[#D4AF37]/10"
              >
                <Plus size={20} />
              </button>
            </div>
            <p className="mt-3 text-xs text-[#C0C0C0]/70">Max 100 tickets per purchase</p>
          </div>

          <div className="text-right md:text-right">
            <p className="text-sm uppercase tracking-[0.24em] text-[#C0C0C0]/70">Total</p>
            <p data-testid="buy-total" className="font-serif-display text-5xl sm:text-6xl font-black text-gold-gradient">
              {formatZAR(total)}
            </p>
            <p className="text-xs text-[#C0C0C0]/70 mt-2">{qty} × {formatZAR(PRICE)}</p>
          </div>
        </div>

        <div className="mt-10 flex flex-col-reverse sm:flex-row justify-end gap-3">
          <Link to="/" className="btn-ghost-gold text-center">Cancel</Link>
          <button
            data-testid="proceed-payment"
            disabled={loggedInUnverified}
            onClick={proceed}
            className="btn-gold"
          >
            Proceed to Payment
          </button>
        </div>

        {!user && (
          <p data-testid="guest-hint" className="mt-5 text-xs text-[#C0C0C0]/70 text-right">
            Not signed in yet — we'll ask for a login or a quick sign up before payment.
          </p>
        )}
      </div>

      <InlineAuthDialog open={authOpen} onOpenChange={setAuthOpen} onSuccess={onAuthSuccess} />
    </div>
  );
}
